<template>
  <div :class="type === 'dashboard' ? 'seg-tabs' : 'flex gap-2 flex-wrap items-center'">
    <template v-if="type === 'dashboard'">
      <template v-for="(item, index) in tabs" :key="index">
        <button
          v-if="isDashboardTab(item) ? shouldShowTab(item.cleaned_key) : true"
          class="seg-tab"
          :class="{
            active_tab: isDashboardTab(item) ? current_tab === item.key : false,
          }"
          :disabled="isDashboardTab(item) ? current_tab == item.key : false"
          :title="isDashboardTab(item) ? labelFor(item).title : undefined"
          @click.prevent="isDashboardTab(item) ? switchOrdersTab(item.apiKey, item.key) : null"
        >
          <p class="leading-none mt-[2px]">
            {{ isDashboardTab(item) ? labelFor(item).label : 'No Name' }}
          </p>
          <span
            class="badge badge-light p-1 px-2 bg-primary-light text-xs text-[#330FC2] font-bold rounded-[4px]"
            >{{ isDashboardTab(item) ? item.count : 0 }}</span
          >
        </button>
      </template>
    </template>
    <template v-else>
      <div class="flex border-b border-line w-max gap-5">
        <template v-for="(tab, idx) in tabs" :key="isModeTab(tab) ? tab.id : idx">
          <button
            v-if="isModeTab(tab)"
            class="flex-1 text-center py-3 font-semibold text-base transition-colors duration-200 relative bg-transparent outline-none border-none"
            :class="[
              currentTab === tab.id ? 'text-primary font-bold' : 'text-ink-mute hover:text-primary',
              'mode-tab-btn',
            ]"
            @click.prevent="switchTab(tab.id)"
          >
            <component
              v-if="tab.icon"
              :is="tab.icon"
              class="size-5 inline-block align-middle mr-1"
            />
            <span>{{ tab.label }}</span>
            <span
              v-if="currentTab === tab.id"
              class="absolute left-0 bottom-0 w-full h-0.5 bg-primary rounded transition-all duration-200"
            ></span>
          </button>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { can } from '@/composables/';
import { DashboardTab } from '@/models';
import { tabLabel, type TabLabel } from '@/utils/dashboardTabLabels';
import { onMounted, ref } from 'vue';
interface Props {
  tabs: DashboardTab[] | { id: string; label: string; icon?: any }[];
  currentTab: string;
  type?: 'dashboard' | 'mode';
}
const props = defineProps<Props>();

const emit = defineEmits(['tab-change']);
const current_tab = ref<string>('all_items_count');

/**
 * AB#21240 — the two queue tabs say who is blocked rather than repeating
 * "Pending" twice. The server's own name stays as the tooltip.
 */
const labelFor = (item: DashboardTab): TabLabel =>
  tabLabel((item as DashboardTab).cleaned_key, (item as DashboardTab).display_name);

// Function to check permissions
const shouldShowTab = (cleaned_key: string): boolean => {
  if (
    cleaned_key === 'pending_for_factory_assignment' &&
    can('assign-factory-to-shareable-orders')
  ) {
    return true;
  }
  // else if (cleaned_key === 'quote_requested' && can('show-quotes-tab')) {
  //   return true;
  // }
  // else if (cleaned_key === 'unresolved_comments' && can('can-resolve-comment')) {
  //   return true;
  // }
  else if (
    cleaned_key === 'pending_merchant_approval' ||
    // cleaned_key === 'urgent_action' ||
    // cleaned_key === 'customer_await' ||
    // cleaned_key === 'pending' ||
    // cleaned_key === 'completed' ||
    // cleaned_key === 'cancelled' ||
    // cleaned_key === 'shipped' ||
    // cleaned_key === 'reorders' ||
    cleaned_key === 'my_orders' ||
    cleaned_key === 'all'
  ) {
    return true; // No specific permission needed
  } else if (
    cleaned_key === 'awaiting_customer_feedback' &&
    can('assign-customer-feedback')
  ) {
    return true; // Assuming this tab should be shown to all users
  }
  return false; // Default: hide tab
};

// Function to switch tabs
const switchOrdersTab = (tabKey: string, orgKey: string) => {
  current_tab.value = orgKey;
  emit('tab-change', tabKey);
};
function switchTab(key: string) {
  emit('tab-change', key);
}

// Add type guards
function isDashboardTab(tab: any): tab is DashboardTab {
  return 'cleaned_key' in tab && 'key' in tab && 'apiKey' in tab;
}
function isModeTab(tab: any): tab is { id: string; label: string; icon?: any } {
  return 'id' in tab && 'label' in tab;
}

onMounted(() => {
  current_tab.value = props.currentTab ? `${props.currentTab}_count` : 'all_items_count';
});
</script>

<style lang="scss" scoped>
/*
  AB#21245 — one segmented control instead of five floating pills.
  AB#21297 — and a quieter one: a soft grey track with a white active pill,
  instead of hairline dividers and a black fill. The black "All" block was the
  heaviest thing on the screen and it was navigation, not an action; on a row
  where only Create Order carries colour, the tabs should read as three views
  of one list and nothing more.
*/
.seg-tabs {
  /* surface-3 == #eef1f2 in light (matches the old #EEF0F3 exactly) and a subtle
     raised dark in dark mode, so the segmented container stops rendering as a
     white box. The active tab (bg-surface) + bright ink + accent badge carry the
     selection in both themes. */
  @apply inline-flex flex-wrap items-center gap-[2px] p-[3px] rounded-[9px] bg-surface-3;
}
.seg-tab {
  @apply flex items-center justify-center gap-2 px-3.5 h-[30px] text-sm whitespace-nowrap
         rounded-[7px] bg-transparent text-ink-soft border-0 transition-colors;
}
.seg-tab:hover:not(:disabled) {
  @apply text-ink;
}
.seg-tab:focus-visible {
  @apply outline outline-2 -outline-offset-2 outline-[var(--accent)];
}
.seg-tab .badge {
  @apply bg-transparent text-ink-mute p-0 px-0 font-semibold;
  font-variant-numeric: tabular-nums;
}
.active_tab {
  @apply bg-surface text-ink font-semibold;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.active_tab .badge {
  @apply text-[var(--accent)];
}
.mode-tab-btn {
  border-radius: 0;
  background: none;
  position: relative;
  border: none;
  cursor: pointer;
  margin-bottom: -1px;
}
.mode-tab-btn:focus {
  outline: none;
}
.mode-tab-btn:hover {
  background: none;
  @apply text-primary;
}
</style>
